/**
 * AIOO Wishlist — localStorage state + reactive UI (no jQuery, no login).
 * Dispatches wishlist:updated on every mutation.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'aioo_wishlist';
  var MAX_ITEMS = 48;
  var GRID_SECTION_ID = 'main-wishlist-grid';
  var BATCH_SIZE = 20;

  function readRaw() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function normalizeItem(id, handle) {
    return {
      id: String(id),
      handle: handle ? String(handle) : ''
    };
  }

  function write(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: {
          items: items.slice(),
          count: items.length
        }
      })
    );
  }

  function read() {
    return readRaw().filter(function (item) {
      return item && item.id;
    });
  }

  function isSaved(id) {
    var sid = String(id);
    return read().some(function (item) {
      return String(item.id) === sid;
    });
  }

  function add(id, handle) {
    if (!id) return read();
    if (isSaved(id)) return read();
    var items = read();
    items.unshift(normalizeItem(id, handle));
    if (items.length > MAX_ITEMS) {
      items = items.slice(0, MAX_ITEMS);
    }
    write(items);
    return items;
  }

  function remove(id) {
    if (!id) return read();
    var sid = String(id);
    var items = read().filter(function (item) {
      return String(item.id) !== sid;
    });
    write(items);
    return items;
  }

  function toggle(id, handle) {
    if (isSaved(id)) {
      remove(id);
      return { added: false, items: read() };
    }
    add(id, handle);
    return { added: true, items: read() };
  }

  function getIds() {
    return read().map(function (item) {
      return item.id;
    });
  }

  function strings() {
    return window.aiooWishlistI18n || {};
  }

  function syncButtons(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      var saved = isSaved(id);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var i18n = strings();
      btn.setAttribute(
        'aria-label',
        saved ? i18n.remove || btn.getAttribute('data-label-remove') || '' : i18n.add || btn.getAttribute('data-label-add') || ''
      );
    });
  }

  function syncHeaderBadge() {
    var count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('is-visible', count > 0);
      if (count > 0) {
        badge.classList.remove('hide');
      }
    });
  }

  function ensureToastHost() {
    var host = document.getElementById('aioo-wishlist-toast');
    if (host) return host;
    host = document.createElement('div');
    host.id = 'aioo-wishlist-toast';
    host.className = 'aioo-wishlist-toast-host';
    host.setAttribute('aria-live', 'polite');
    host.setAttribute('aria-atomic', 'true');
    document.body.appendChild(host);
    return host;
  }

  var toastTimer = null;

  function showToast(message) {
    if (!message) return;
    var host = ensureToastHost();
    if (toastTimer) {
      clearTimeout(toastTimer);
      host.innerHTML = '';
    }
    var toast = document.createElement('div');
    toast.className = 'aioo-wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      '<svg class="aioo-wishlist-toast__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">' +
      '<path d="M12 20.25s-6.9-4.35-9.33-8.04C.86 9.36 2.18 5.9 5.6 5.18c1.95-.4 3.88.45 5.02 1.92.47.66 1.31.66 1.78 0C13.54 5.63 15.47 4.78 17.42 5.18c3.42.72 4.74 4.18 2.93 7.03C18.9 15.9 12 20.25 12 20.25z"/>' +
      '</svg>' +
      '<span>' + message + '</span>';
    host.appendChild(toast);
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 280);
    }, 2600);
  }

  function buildSearchQuery(items) {
    return items
      .map(function (item) {
        if (item.handle) {
          return 'handle:' + item.handle;
        }
        return 'id:' + item.id;
      })
      .join('+OR+');
  }

  function parseSectionHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.querySelector('[data-wishlist-grid-root]') ||
      doc.getElementById('shopify-section-' + GRID_SECTION_ID) ||
      doc.querySelector('.shopify-section')
    );
  }

  var gridRenderToken = 0;

  function setPageBlock(el, visible) {
    if (!el) return;
    el.hidden = !visible;
    el.setAttribute('aria-hidden', visible ? 'false' : 'true');
    el.classList.toggle('is-visible', visible);
  }

  function fetchGridBatch(items) {
    if (!items.length) return Promise.resolve('');
    var base = (window.routes && window.routes.url) || window.location.origin;
    var url =
      base +
      '/search?q=' +
      buildSearchQuery(items) +
      '&type=product&section_id=' +
      GRID_SECTION_ID;
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      return res.text();
    });
  }

  function renderWishlistGrid() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return Promise.resolve();

    var gridHost = page.querySelector('[data-wishlist-grid]');
    var emptyState = page.querySelector('[data-wishlist-empty]');
    var loadingState = page.querySelector('[data-wishlist-loading]');
    var items = read();

    if (!gridHost) return Promise.resolve();

    var token = ++gridRenderToken;

    if (!items.length) {
      gridHost.innerHTML = '';
      setPageBlock(gridHost, false);
      setPageBlock(emptyState, true);
      setPageBlock(loadingState, false);
      return Promise.resolve();
    }

    setPageBlock(emptyState, false);
    setPageBlock(loadingState, true);
    setPageBlock(gridHost, false);

    var batches = [];
    for (var i = 0; i < items.length; i += BATCH_SIZE) {
      batches.push(items.slice(i, i + BATCH_SIZE));
    }

    return batches
      .reduce(function (chain, batch) {
        return chain.then(function (acc) {
          return fetchGridBatch(batch).then(function (html) {
            acc.push(html);
            return acc;
          });
        });
      }, Promise.resolve([]))
      .then(function (htmlParts) {
        if (token !== gridRenderToken) return;

        var combined = document.createElement('div');
        combined.className = 'grid rwcols-2 rwcols-md-3 rwcols-lg-4 grid-products aioo-wishlist-grid';

        htmlParts.forEach(function (html) {
          var sectionNode = parseSectionHtml(html);
          if (!sectionNode) return;
          var innerGrid = sectionNode.querySelector('.grid-products');
          if (innerGrid) {
            while (innerGrid.firstChild) {
              combined.appendChild(innerGrid.firstChild);
            }
          }
        });

        gridHost.innerHTML = '';
        setPageBlock(loadingState, false);

        if (combined.childElementCount > 0) {
          gridHost.appendChild(combined);
          setPageBlock(gridHost, true);
          setPageBlock(emptyState, false);
          syncButtons(gridHost);
          document.dispatchEvent(new CustomEvent('wishlist:grid-rendered'));
        } else {
          setPageBlock(gridHost, false);
          setPageBlock(emptyState, true);
        }
      })
      .catch(function () {
        if (token !== gridRenderToken) return;
        setPageBlock(loadingState, false);
        setPageBlock(gridHost, false);
        setPageBlock(emptyState, true);
        gridHost.innerHTML = '';
      });
  }

  function onWishlistUpdated() {
    syncButtons();
    syncHeaderBadge();
    if (document.querySelector('[data-wishlist-page]')) {
      renderWishlistGrid();
    }
  }

  function handleToggleClick(btn) {
    var id = btn.getAttribute('data-product-id');
    var handle = btn.getAttribute('data-product-handle') || '';
    var result = toggle(id, handle);
    var i18n = strings();
    showToast(result.added ? i18n.added : i18n.removed);
    if (result.added) {
      btn.classList.add('is-pulsing');
      setTimeout(function () {
        btn.classList.remove('is-pulsing');
      }, 520);
    }
  }

  function bindEvents() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      handleToggleClick(btn);
    });

    document.addEventListener('wishlist:updated', onWishlistUpdated);

    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) onWishlistUpdated();
    });
  }

  function init() {
    bindEvents();
    syncButtons();
    syncHeaderBadge();
    renderWishlistGrid();
  }

  window.AiooWishlist = {
    STORAGE_KEY: STORAGE_KEY,
    read: read,
    add: add,
    remove: remove,
    toggle: toggle,
    isSaved: isSaved,
    getIds: getIds,
    syncButtons: syncButtons,
    syncHeaderBadge: syncHeaderBadge,
    renderWishlistGrid: renderWishlistGrid,
    showToast: showToast
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
